import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { downloadPdf } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  Save,
  Send,
  Plus,
  X,
  ChevronUp,
  ChevronDown,
  Users,
  CheckCircle2,
  Loader2,
  Sparkles,
  History as HistoryIcon,
  ScrollText,
  ArrowUpRight,
  Download,
} from "lucide-react";

const PREAMBULATORY_HINTS = [
  "Recalling…", "Recognising…", "Acknowledging…", "Reaffirming…", "Noting…",
  "Welcoming…", "Bearing in mind…", "Deeply concerned…", "Emphasising…", "Considering…",
];
const OPERATIVE_HINTS = [
  "Calls upon…", "Urges…", "Recommends…", "Encourages…", "Requests…",
  "Decides to…", "Authorises…", "Endorses…", "Establishes…", "Demands…",
];

export default function ResolutionBuilderPage() {
  const { resolutionId } = useParams();
  const [resolution, setResolution] = useState({
    event_id: "",
    committee_id: "",
    title: "",
    topic: "",
    preambulatory: [],
    operative: [],
    co_sponsors: [],
    signatories: [],
  });
  const [savedRes, setSavedRes] = useState(null);
  const [events, setEvents] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [autoStatus, setAutoStatus] = useState("idle");
  const [coInput, setCoInput] = useState("");
  const [sigInput, setSigInput] = useState("");
  const [versions, setVersions] = useState([]);
  const debounceRef = useRef();

  useEffect(() => {
    if (resolutionId) {
      api.get(`/resolutions/${resolutionId}`).then((r) => {
        const d = r.data;
        setResolution({
          event_id: d.event_id, committee_id: d.committee_id, title: d.title || "",
          topic: d.topic || "",
          preambulatory: d.preambulatory || [], operative: d.operative || [],
          co_sponsors: d.co_sponsors || [], signatories: d.signatories || [],
        });
        setSavedRes(d);
        setVersions(d.versions || []);
      }).catch(() => toast.error("Couldn't load resolution"));
    } else {
      api.get("/registrations").then((r) => {
        const reg = r.data?.[0];
        if (reg) {
          setResolution((p) => ({
            ...p,
            event_id: reg.event_id,
            committee_id: reg.council_id || "",
            title: reg.country ? `Resolution by ${reg.country}` : "",
          }));
          if (reg.country) {
            setResolution((p) => ({ ...p, co_sponsors: p.co_sponsors.length ? p.co_sponsors : [reg.country] }));
          }
        }
      }).catch(() => {});
    }
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {});
  }, [resolutionId]);

  useEffect(() => {
    if (resolution.event_id) {
      api.get(`/events/${resolution.event_id}`).then((r) => setCommittees(r.data?.councils || [])).catch(() => {});
    }
  }, [resolution.event_id]);

  const save = useCallback(async (silent = false) => {
    if (!resolution.event_id || !resolution.committee_id || !resolution.title) {
      if (!silent) toast.error("Event, committee & title are required");
      return null;
    }
    setAutoStatus("saving");
    try {
      const { data } = await api.post("/resolutions/draft", resolution);
      setSavedRes(data);
      setAutoStatus("saved");
      if (!silent) toast.success(`Saved · v${data.version}`);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
      setAutoStatus("idle");
      return null;
    }
  }, [resolution]);

  useEffect(() => {
    if (!savedRes) return;
    clearTimeout(debounceRef.current);
    setAutoStatus("idle");
    debounceRef.current = setTimeout(() => save(true), 1800);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution.preambulatory, resolution.operative, resolution.co_sponsors, resolution.signatories, resolution.title, resolution.topic]);

  const submit = async () => {
    const saved = savedRes || (await save(true));
    if (!saved) return;
    try {
      await api.post("/resolutions/submit", { resolution_id: saved.id });
      toast.success("Resolution submitted to chair for review");
      const r = await api.get(`/resolutions/${saved.id}`);
      setSavedRes(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit");
    }
  };

  const addClause = (kind) => {
    setResolution({ ...resolution, [kind]: [...resolution[kind], ""] });
  };
  const editClause = (kind, idx, v) => {
    const next = [...resolution[kind]];
    next[idx] = v;
    setResolution({ ...resolution, [kind]: next });
  };
  const removeClause = (kind, idx) => {
    const next = resolution[kind].filter((_, i) => i !== idx);
    setResolution({ ...resolution, [kind]: next });
  };
  const moveClause = (kind, idx, dir) => {
    const next = [...resolution[kind]];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setResolution({ ...resolution, [kind]: next });
  };

  const addCoSponsor = (e) => {
    e?.preventDefault?.();
    const v = coInput.trim();
    if (!v) return;
    if (!resolution.co_sponsors.includes(v))
      setResolution({ ...resolution, co_sponsors: [...resolution.co_sponsors, v] });
    setCoInput("");
  };
  const addSignatory = (e) => {
    e?.preventDefault?.();
    const v = sigInput.trim();
    if (!v) return;
    if (!resolution.signatories.includes(v))
      setResolution({ ...resolution, signatories: [...resolution.signatories, v] });
    setSigInput("");
  };

  const submitted = savedRes?.status === "submitted";

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-16">
        {/* Top bar */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Resolution Builder
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              {resolution.title || "New resolution"}
            </h1>
            <div className="mt-2 flex items-center gap-3 text-xs text-slate-500" data-testid="res-status">
              {savedRes && (
                <>
                  <span className="font-mono">v{savedRes.version}</span>
                  <span>·</span>
                  <ResStatus status={savedRes.status} review={savedRes.chair_review_status} />
                </>
              )}
              <span className="inline-flex items-center gap-1.5">
                {autoStatus === "saving" && <Loader2 className="h-3 w-3 animate-spin" />}
                {autoStatus === "saved" && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                {autoStatus === "idle" && savedRes && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
                <span>
                  {autoStatus === "saving" ? "Saving…" : autoStatus === "saved" ? "Auto-saved" : savedRes ? "Edits pending" : "Not saved"}
                </span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/dashboard/resolutions" className="text-xs text-slate-600 hover:text-[#0F4C81] underline-offset-2 hover:underline">
              All my resolutions
            </Link>
            <Link to="/dashboard/ai-prep">
              <Button variant="outline" className="border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white" data-testid="open-prep-from-res">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> AI Prep
              </Button>
            </Link>
            <Button onClick={() => save(false)} variant="outline" className="border-slate-300" data-testid="res-save-button">
              <Save className="h-3.5 w-3.5 mr-1.5" /> Save
            </Button>
            <Button
              onClick={async () => {
                if (!savedRes?.id) {
                  toast.error("Save the draft first to download a PDF.");
                  return;
                }
                try {
                  await downloadPdf(`/resolutions/${savedRes.id}/pdf`);
                  toast.success("Resolution PDF downloaded");
                } catch {
                  toast.error("Could not generate PDF.");
                }
              }}
              variant="outline"
              className="border-[#0F4C81] text-[#0F4C81]"
              data-testid="res-pdf-button"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> PDF
            </Button>
            <Button
              onClick={submit}
              disabled={submitted}
              className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
              data-testid="res-submit-button"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" /> {submitted ? "Submitted" : "Submit"}
            </Button>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          {/* Editor */}
          <div className="lg:col-span-8 space-y-5">
            {/* Header */}
            <div className="rounded-2xl bg-white border border-slate-200 p-6 grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs">Resolution title</Label>
                <Input value={resolution.title} onChange={(e) => setResolution({ ...resolution, title: e.target.value })} data-testid="res-title" />
              </div>
              <div>
                <Label className="text-xs">Event</Label>
                <select
                  value={resolution.event_id}
                  onChange={(e) => setResolution({ ...resolution, event_id: e.target.value })}
                  className="mt-1.5 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="res-event"
                >
                  <option value="">Select event…</option>
                  {events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Committee</Label>
                <select
                  value={resolution.committee_id || ""}
                  onChange={(e) => setResolution({ ...resolution, committee_id: e.target.value })}
                  className="mt-1.5 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="res-committee"
                >
                  <option value="">Select committee…</option>
                  {committees.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Topic / agenda</Label>
                <Input value={resolution.topic} onChange={(e) => setResolution({ ...resolution, topic: e.target.value })} data-testid="res-topic" />
              </div>
            </div>

            {/* Preambulatory */}
            <ClauseSection
              title="Preambulatory clauses"
              testid="preambulatory"
              clauses={resolution.preambulatory}
              hints={PREAMBULATORY_HINTS}
              onAdd={() => addClause("preambulatory")}
              onEdit={(i, v) => editClause("preambulatory", i, v)}
              onRemove={(i) => removeClause("preambulatory", i)}
              onMove={(i, d) => moveClause("preambulatory", i, d)}
              numberPrefix=""
              italic
            />

            {/* Operative */}
            <ClauseSection
              title="Operative clauses"
              testid="operative"
              clauses={resolution.operative}
              hints={OPERATIVE_HINTS}
              onAdd={() => addClause("operative")}
              onEdit={(i, v) => editClause("operative", i, v)}
              onRemove={(i) => removeClause("operative", i)}
              onMove={(i, d) => moveClause("operative", i, d)}
              numbered
            />
          </div>

          {/* Right rail */}
          <div className="lg:col-span-4 space-y-5">
            {/* Co-sponsors & Signatories */}
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-[#0F4C81]" />
                <p className="text-sm font-semibold text-[#0A192F]">Co-sponsors</p>
                <span className="text-xs text-slate-500">({resolution.co_sponsors.length})</span>
              </div>
              <form onSubmit={addCoSponsor} className="mt-3 flex gap-2">
                <Input
                  placeholder="Add country…"
                  value={coInput}
                  onChange={(e) => setCoInput(e.target.value)}
                  className="h-9"
                  data-testid="res-cosponsor-input"
                />
                <Button type="submit" size="sm" className="bg-[#0F4C81] hover:bg-[#0A3860] h-9 px-3" data-testid="res-cosponsor-add">
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </form>
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="res-cosponsors-list">
                {resolution.co_sponsors.length === 0 ? (
                  <p className="text-xs text-slate-400">No sponsors yet.</p>
                ) : resolution.co_sponsors.map((c, i) => (
                  <span key={c} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#D4AF37]/15 text-[#0A192F] text-xs font-bold" data-testid="cosponsor-pill">
                    {c}
                    <button onClick={() => setResolution({ ...resolution, co_sponsors: resolution.co_sponsors.filter((_, j) => j !== i) })} className="ml-1 hover:text-red-600">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>

              <div className="mt-5 pt-5 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <ScrollText className="h-4 w-4 text-[#0F4C81]" />
                  <p className="text-sm font-semibold text-[#0A192F]">Signatories</p>
                  <span className="text-xs text-slate-500">({resolution.signatories.length})</span>
                </div>
                <form onSubmit={addSignatory} className="mt-3 flex gap-2">
                  <Input
                    placeholder="Add country…"
                    value={sigInput}
                    onChange={(e) => setSigInput(e.target.value)}
                    className="h-9"
                    data-testid="res-signatory-input"
                  />
                  <Button type="submit" size="sm" className="bg-slate-700 hover:bg-slate-900 h-9 px-3">
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </form>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {resolution.signatories.length === 0 ? (
                    <p className="text-xs text-slate-400">No signatories yet.</p>
                  ) : resolution.signatories.map((c, i) => (
                    <span key={c} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs">
                      {c}
                      <button onClick={() => setResolution({ ...resolution, signatories: resolution.signatories.filter((_, j) => j !== i) })} className="ml-1 hover:text-red-600">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Chair review status */}
            {savedRes?.chair_review_status && savedRes.chair_review_status !== "not_submitted" && (
              <div className="rounded-2xl bg-[#0A192F] text-white p-5 border border-[#D4AF37]/20">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  Chair review
                </p>
                <p className="mt-2 text-sm font-semibold">{savedRes.chair_review_status.replace("_", " ")}</p>
                {savedRes.chair_feedback && (
                  <p className="mt-2 text-xs text-slate-300 italic">"{savedRes.chair_feedback}"</p>
                )}
                {savedRes.reviewed_by_name && (
                  <p className="mt-2 text-[10px] text-slate-400">— {savedRes.reviewed_by_name}</p>
                )}
              </div>
            )}

            {/* Versions */}
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="flex items-center gap-2">
                <HistoryIcon className="h-4 w-4 text-[#0F4C81]" />
                <p className="text-sm font-semibold text-[#0A192F]">Version history</p>
              </div>
              {(versions || []).length === 0 ? (
                <p className="mt-3 text-xs text-slate-400">No previous versions yet.</p>
              ) : (
                <ul className="mt-3 space-y-2 max-h-72 overflow-y-auto">
                  {versions.map((v) => (
                    <li key={v.id} className="text-xs flex items-center justify-between p-2 rounded-md bg-slate-50">
                      <span className="font-mono text-slate-700">v{v.version}</span>
                      <span className="text-slate-500">{v.saved_at ? new Date(v.saved_at).toLocaleString() : "—"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function ClauseSection({ title, testid, clauses, hints, onAdd, onEdit, onRemove, onMove, numbered = false, italic = false }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`section-${testid}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
            Section
          </p>
          <h3 className="mt-1 text-lg font-semibold tracking-tight text-[#0A192F]">{title}</h3>
        </div>
        <Button onClick={onAdd} size="sm" className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid={`add-${testid}`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add clause
        </Button>
      </div>
      {clauses.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">No clauses yet. Click "Add clause" or use the suggestion chips below.</p>
      ) : (
        <div className="mt-5 space-y-3" data-testid={`${testid}-list`}>
          {clauses.map((c, i) => (
            <div key={i} className="group rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start gap-3" data-testid={`${testid}-clause-${i}`}>
              <div className="flex flex-col items-center gap-1 pt-1">
                {numbered && <span className="font-mono text-xs text-slate-500">{i + 1}.</span>}
                <button onClick={() => onMove(i, -1)} className="text-slate-400 hover:text-[#0F4C81] disabled:opacity-30" disabled={i === 0}>
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => onMove(i, 1)} className="text-slate-400 hover:text-[#0F4C81] disabled:opacity-30" disabled={i === clauses.length - 1}>
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>
              <Textarea
                rows={2}
                value={c}
                onChange={(e) => onEdit(i, e.target.value)}
                className={`flex-1 bg-white ${italic ? "italic" : ""}`}
                placeholder="Type clause…"
              />
              <button onClick={() => onRemove(i)} className="text-slate-400 hover:text-red-600 mt-2" aria-label="Remove clause">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Suggestion chips</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {hints.map((h, hIdx) => (
            <button
              key={h}
              onClick={() => {
                onAdd();
                setTimeout(() => onEdit(clauses.length, h + " "), 0);
              }}
              className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-[#D4AF37]/20 text-xs text-slate-700"
              data-testid={`hint-${testid}-${hIdx}`}
            >
              {h}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ResStatus({ status, review }) {
  if (status === "submitted") {
    if (review === "accepted") return <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Accepted</span>;
    if (review === "rejected") return <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Rejected</span>;
    if (review === "needs_revision") return <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Needs Revision</span>;
    return <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Submitted · Awaiting</span>;
  }
  return <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">Draft</span>;
}
